import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { isSiteScopedPath } from '@/lib/siteScopedRoutes';

const DEFAULT = '贴图虾 · 信息后台';

/** 未登录或登录页：不拼站点名 */
const PUBLIC_PATHS = new Set(['/', '/login', '/m/login']);

/** 与浏览器标签同步，便于多开窗口时区分页面 */
const TITLE_BY_PATH: Record<string, string> = {
  '/': DEFAULT,
  '/login': '登录 · 贴图虾',
  '/m/login': '登录（手机）· 贴图虾',
  '/dashboard': '首页 · 贴图虾',
  '/entries': '信息登记 · 贴图虾',
  '/register': '登记信息 · 贴图虾',
  '/sites': '站点管理 · 贴图虾',
  '/users': '用户管理 · 贴图虾',
  '/log-center': '日志中心 · 贴图虾',
  '/login-logs': '系统登录日志 · 贴图虾',
  '/settings': '内容与样式 · 贴图虾',
  '/wechat': '微信公众号 · 贴图虾',
  '/interfaces': '接入与推送 · 贴图虾',
  '/help': '帮助 · 贴图虾',
  '/oss/guide': '阿里云 OSS 开通指引 · 贴图虾',
  '/oss': 'OSS 参数设置 · 贴图虾',
  '/miniprograms': '小程序管理 · 贴图虾',
  '/robot-forward-logs': '小程序转发日志 · 贴图虾',
  '/micro-promotion-forward-logs': '微推广转发日志 · 贴图虾',
  '/micro-promotion-wework-push-logs': '微推广企微推送日志 · 贴图虾',
  '/promotion-areas': '微推广管理 · 贴图虾',
};

const SITE_NAME_MAX = 26;

function normalizedPath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  const trimmed = pathname.replace(/\/+$/, '') || '/';
  if (trimmed === '/m/login') return '/m/login';
  if (trimmed === '/m' || trimmed === '/m/') return '/dashboard';
  if (trimmed.startsWith('/m/')) {
    const inner = trimmed.replace(/^\/m\//, '/');
    const seg = inner.replace(/^\//, '').split('/')[0];
    return seg ? `/${seg}` : '/dashboard';
  }
  return '/' + trimmed.replace(/^\//, '').split('/')[0];
}

function truncateSiteName(name: string): string {
  const t = name.trim();
  if (t.length <= SITE_NAME_MAX) return t;
  return `${t.slice(0, SITE_NAME_MAX - 1)}…`;
}

/** 在末尾「· 贴图虾」前插入当前站点：「首页 · 贴图虾」→「首页 · 站点A · 贴图虾」 */
function withCurrentSite(title: string, siteName: string): string {
  const sep = ' · ';
  const last = title.lastIndexOf(sep);
  if (last === -1) {
    return `${truncateSiteName(siteName)} · ${title}`;
  }
  const left = title.slice(0, last);
  const right = title.slice(last + sep.length);
  return `${left}${sep}${truncateSiteName(siteName)}${sep}${right}`;
}

export function DocumentTitle() {
  const { pathname } = useLocation();
  const { ok, loading, currentSiteId, currentSiteName } = useSession();

  useEffect(() => {
    const key = normalizedPath(pathname);
    let title = TITLE_BY_PATH[key] ?? DEFAULT;

    const inApp =
      !loading &&
      ok &&
      !PUBLIC_PATHS.has(key) &&
      isSiteScopedPath(pathname) &&
      currentSiteId >= 1 &&
      currentSiteName.trim() !== '';

    if (inApp) {
      title = withCurrentSite(title, currentSiteName);
    }

    document.title = title;
  }, [pathname, loading, ok, currentSiteId, currentSiteName]);

  return null;
}
